<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2 flex-wrap">
      <slot />
      <!-- Status Filter -->
      <Select v-model="selectedStatus">
        <SelectTrigger
          class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
          :class="selectedStatus && selectedStatus !== 'all' && selectedStatus !== 'true'
            ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
            : 'border-gray-200 text-gray-600'"
        >
          <Briefcase class="w-3 h-3 shrink-0" />
          <SelectValue placeholder="All jobs" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in statusOptions"
            :key="option.value || 'all'"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Date Added Filter -->
      <Select v-model="selectedDate">
        <SelectTrigger
          class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
          :class="selectedDate && selectedDate !== 'all'
            ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
            : 'border-gray-200 text-gray-600'"
        >
          <CalendarDays class="w-3 h-3 shrink-0" />
          <SelectValue placeholder="All time" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in dateOptions"
            :key="option.value || 'all'"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Experience Level Filter -->
      <Select v-model="selectedExperience">
        <SelectTrigger
          class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
          :class="selectedExperience && selectedExperience !== 'all'
            ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
            : 'border-gray-200 text-gray-600'"
        >
          <GraduationCap class="w-3 h-3 shrink-0" />
          <SelectValue placeholder="Experience level" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in experienceOptions"
            :key="option.value || 'all'"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Employer Filter -->
      <Combobox v-model="selectedEmployers" multiple>
        <ComboboxAnchor as-child>
          <ComboboxTrigger as-child>
            <button
              class="inline-flex items-center gap-1.5 h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors"
              :class="selectedEmployers.length
                ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
                : 'border-gray-200 text-gray-600'"
            >
              <Building2 class="w-3 h-3 shrink-0" />
              Employer
              <span
                v-if="selectedEmployers.length"
                class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
              >{{ selectedEmployers.length }}</span>
              <ChevronDown class="w-3 h-3 opacity-40" />
            </button>
          </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxList ref="employerOptionsContainer" class="max-h-[200px] overflow-y-auto" @scroll="handleEmployerScroll">
          <div class="sticky top-0 z-10 bg-popover w-full items-center">
            <ComboboxInput 
              v-model="employerSearch"
              class="pl-9 focus-visible:ring-0 border-0 border-b rounded-none h-10" 
              placeholder="Search employers..." 
            />
            <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
              <Search class="size-4 text-muted-foreground" />
            </span>
          </div>

          <ComboboxEmpty>
            No employers found.
          </ComboboxEmpty>

          <ComboboxGroup>
            <div v-if="employerStore.isLoading" class="flex justify-center py-2">
              <div class="animate-spin rounded-full h-4 w-4 border-2 border-primary"></div>
            </div>
            <ComboboxItem
              v-for="(employer, index) in filteredEmployers"
              :key="employer.id"
              :value="employer"
              :ref="index === filteredEmployers.length - 1 ? 'lastEmployerItem' : undefined"
            >
              {{ employer.name }}
              <ComboboxItemIndicator>
                <Check :class="cn('ml-auto h-4 w-4')" />
              </ComboboxItemIndicator>
            </ComboboxItem>
            <div v-if="employerStore.nextPage && !employerStore.isLoading" class="text-center text-sm text-gray-500 py-2">
              Scroll for more...
            </div>
          </ComboboxGroup>
        </ComboboxList>
      </Combobox>

      <!-- Country/area Filter -->
      <Combobox v-model="selectedCountries" multiple>
        <ComboboxAnchor as-child>
          <ComboboxTrigger as-child>
            <button
              class="inline-flex items-center gap-1.5 h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors"
              :class="selectedCountries.length
                ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
                : 'border-gray-200 text-gray-600'"
            >
              <Globe class="w-3 h-3 shrink-0" />
              Country
              <span
                v-if="selectedCountries.length"
                class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
              >{{ selectedCountries.length }}</span>
              <ChevronDown class="w-3 h-3 opacity-40" />
            </button>
          </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxList class="max-h-[250px]">
          <div class="sticky top-0 z-10 bg-popover w-full items-center">
            <ComboboxInput 
              v-model="countrySearch"
              class="pl-9 focus-visible:ring-0 border-0 border-b rounded-none h-10" 
              placeholder="Search countries..." 
            />
            <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
              <Search class="size-4 text-muted-foreground" />
            </span>
          </div>

          <ComboboxEmpty>
            No countries found.
          </ComboboxEmpty>

          <ComboboxGroup>
            <ComboboxItem
              v-for="country in filteredCountries"
              :key="country.id"
              :value="country"
            >
              {{ country.name }}
              <ComboboxItemIndicator>
                <Check :class="cn('ml-auto h-4 w-4')" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </ComboboxGroup>
        </ComboboxList>
      </Combobox>

      <!-- Location Filter -->
      <Combobox v-model="selectedLocations" multiple>
        <ComboboxAnchor as-child>
          <ComboboxTrigger as-child>
            <button
              class="inline-flex items-center gap-1.5 h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors"
              :class="selectedLocations.length
                ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
                : 'border-gray-200 text-gray-600'"
            >
              <MapPin class="w-3 h-3 shrink-0" />
              Location
              <span
                v-if="selectedLocations.length"
                class="ml-0.5 bg-cosmicPurple text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-semibold"
              >{{ selectedLocations.length }}</span>
              <ChevronDown class="w-3 h-3 opacity-40" />
            </button>
          </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxList class="max-h-[250px]">
          <div class="sticky top-0 z-10 bg-popover w-full items-center">
            <ComboboxInput 
              v-model="locationSearch"
              class="pl-9 focus-visible:ring-0 border-0 border-b rounded-none h-10" 
              placeholder="Search locations..." 
            />
            <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
              <Search class="size-4 text-muted-foreground" />
            </span>
          </div>

          <ComboboxEmpty>
            No locations found.
          </ComboboxEmpty>

          <ComboboxGroup v-for="group in filteredLocationGroups" :key="group.id">
            <div class="px-2 py-1.5 text-xs font-medium text-gray-500">
              {{ group.name }}
            </div>
            <ComboboxItem
              v-for="location in group.locations"
              :key="location.id"
              :value="location"
            >
              {{ location.location_name }}
              <ComboboxItemIndicator>
                <Check :class="cn('ml-auto h-4 w-4')" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </ComboboxGroup>
        </ComboboxList>
      </Combobox>

      <!-- Clear all -->
      <button
        v-if="isAnyFilterActive"
        @click="clearFilters"
        class="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-full border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-500 transition-colors"
      >
        <X class="w-3 h-3" />
        Clear all
      </button>
    </div>

    <!-- Active filter chips -->
    <div
      v-if="selectedEmployers.length || selectedCountries.length || selectedLocations.length"
      class="flex flex-wrap gap-1.5"
    >
      <span
        v-for="employer in selectedEmployers"
        :key="`emp-${employer.id}`"
        class="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs rounded-full bg-cosmicPurple/10 text-cosmicPurple border border-cosmicPurple/20"
      >
        {{ employer.name }}
        <button
          @click="removeEmployer(employer)"
          class="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-cosmicPurple/20 transition-colors"
        >
          <X class="w-2.5 h-2.5" />
        </button>
      </span>
      <span
        v-for="country in selectedCountries"
        :key="`country-${country.id}`"
        class="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs rounded-full bg-cosmicPurple/10 text-cosmicPurple border border-cosmicPurple/20"
      >
        {{ country.name }}
        <button
          @click="removeCountry(country)"
          class="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-cosmicPurple/20 transition-colors"
        >
          <X class="w-2.5 h-2.5" />
        </button>
      </span>
      <span
        v-for="location in selectedLocations"
        :key="`loc-${location.id}`"
        class="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs rounded-full bg-cosmicPurple/10 text-cosmicPurple border border-cosmicPurple/20"
      >
        {{ location.location_name }}
        <button
          @click="removeLocation(location)"
          class="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-cosmicPurple/20 transition-colors"
        >
          <X class="w-2.5 h-2.5" />
        </button>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useJobLocationsStore } from '@/stores/jobLocations'
import { X, ChevronDown, Search, Check, Briefcase, CalendarDays, GraduationCap, Building2, Globe, MapPin } from 'lucide-vue-next'
import { useEmployerStore } from '@/stores/employer'
import { statusOptions, dateOptions, experienceOptions } from '@/const/jobFilterOptions'
import { cn } from '@/lib/utils'
// api imports removed because we no longer fetch employer names on init

// Import shadcn-vue Select components for status, date, and experience
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

// Import ComboBox components for employer, country, and location
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxTrigger
} from '@/components/ui/combobox'

const jobLocationsStore = useJobLocationsStore()
const route = useRoute()
const router = useRouter()
const employerStore = useEmployerStore()

// Filter state
const selectedStatus = ref('')
const selectedDate = ref('')
const selectedExperience = ref('')
const selectedEmployers = ref<any[]>([])
const selectedCountries = ref<any[]>([])
const selectedLocations = ref<any[]>([])

// Track if the employer filter was modified by the user
const employerFilterTouched = ref(false)

// Guard to prevent URL updates while syncing state from the route
const isInitializingFromURL = ref(false)

const isAnyFilterActive = computed(() =>
  (selectedStatus.value && selectedStatus.value !== 'all' && selectedStatus.value !== 'true') ||
  (selectedDate.value && selectedDate.value !== 'all') ||
  (selectedExperience.value && selectedExperience.value !== 'all') ||
  selectedEmployers.value.length > 0 ||
  selectedCountries.value.length > 0 ||
  selectedLocations.value.length > 0
)

// Search states
const employerSearch = ref('')
const countrySearch = ref('')
const locationSearch = ref('')

// Computed filtered options
const filteredEmployers = computed(() => {
  // Use employerOptions for search results, fallback to employers for initial load
  const sourceEmployers = employerStore.employerOptions.length > 0 ? employerStore.employerOptions : employerStore.employers
  if (!employerSearch.value) return sourceEmployers
  
  // If we have a search term, return the API search results
  return sourceEmployers
})

const filteredCountries = computed(() => {
  const countryGroups = jobLocationsStore.jobLocations.groups.filter((group: any) => group.type === 'country')
  if (!countrySearch.value) return countryGroups
  return countryGroups.filter((country: any) =>
    country.name.toLowerCase().includes(countrySearch.value.toLowerCase())
  )
})

const filteredLocationGroups = computed(() => {
  const allGroups = jobLocationsStore.jobLocations.groups
  let groups = allGroups

  // If countries are selected, only include groups for those countries
  if (selectedCountries.value.length > 0) {
    const selectedCountryIds = new Set(selectedCountries.value.map((c: any) => c.id))
    groups = groups.filter((group: any) => selectedCountryIds.has(group.id))
  }

  // Filter by search term if provided
  if (locationSearch.value) {
    const term = locationSearch.value.toLowerCase()
    groups = groups
      .map((group: any) => ({
        ...group,
        locations: group.locations.filter((location: any) =>
          location.location_name.toLowerCase().includes(term)
        )
      }))
      .filter((group: any) => group.locations.length > 0)
  }

  return groups
})

// Initialize from URL
const initializeFromURL = async () => {
  isInitializingFromURL.value = true
  try {
    const query = route.query
    
    selectedStatus.value = (query.status as string) || 'true'
    selectedDate.value = (query.date as string) || 'all'
    selectedExperience.value = (query.experience as string) || 'all'
    
    // Do not initialize employer UI selection from URL.
    // We intentionally preserve employer_ids in the URL for fetching, but avoid pre-populating the UI selection
    // to prevent expansion/replacement issues when only the first page of options is loaded.
    
    // Handle countries from URL
    if (query.country_ids) {
      const countryIds = Array.isArray(query.country_ids)
        ? query.country_ids.map(Number)
        : [Number(query.country_ids)]
      const countryGroups = jobLocationsStore.jobLocations.groups.filter((group: any) => group.type === 'country')
      selectedCountries.value = countryGroups.filter((country: any) => countryIds.includes(country.id))
    } else {
      selectedCountries.value = []
    }
    
    // Handle locations from URL
    if (query.location_ids) {
      const locationIds = Array.isArray(query.location_ids)
        ? query.location_ids.map(Number)
        : [Number(query.location_ids)]
      const allLocations = jobLocationsStore.jobLocations.groups.flatMap((group: any) => group.locations)
      selectedLocations.value = allLocations.filter((location: any) => locationIds.includes(location.id))
    } else {
      selectedLocations.value = []
    }
  } finally {
    // Ensure any watchers reacting to the above state changes run while the guard is active
    await nextTick()
    isInitializingFromURL.value = false
  }
}

// Update URL with current filters
const updateURL = () => {
  const newQuery: Record<string, any> = { ...route.query }
  
  // Update status
  if (selectedStatus.value && selectedStatus.value !== 'true') {
    newQuery.status = selectedStatus.value
  } else {
    newQuery.status = 'true'
  }
  
  // Update date
  if (selectedDate.value && selectedDate.value !== 'all') {
    newQuery.date = selectedDate.value
  } else {
    delete newQuery.date
  }
  
  // Update experience
  if (selectedExperience.value && selectedExperience.value !== 'all') {
    newQuery.experience = selectedExperience.value
  } else {
    delete newQuery.experience
  }
  
  // Update employers
  // Preserve existing employer_ids unless the user has interacted with the employer filter
  if (employerFilterTouched.value) {
    if (selectedEmployers.value.length > 0) {
      newQuery.employer_ids = selectedEmployers.value.map(emp => emp.id)
    } else {
      delete newQuery.employer_ids
    }
  }
  
  // Update countries
  if (selectedCountries.value.length > 0) {
    newQuery.country_ids = selectedCountries.value.map(country => country.id)
  } else {
    delete newQuery.country_ids
  }
  
  // Update locations
  if (selectedLocations.value.length > 0) {
    newQuery.location_ids = selectedLocations.value.map(location => location.id)
  } else {
    delete newQuery.location_ids
  }
  
  // Reset to page 1 when filters change
  newQuery.page = '1'
  
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({ query: newQuery })
}

// Filter change handlers
const clearFilters = () => {
  selectedStatus.value = 'true'
  selectedDate.value = 'all'
  selectedExperience.value = 'all'
  selectedEmployers.value = []
  employerFilterTouched.value = true
  selectedCountries.value = []
  selectedLocations.value = []
  updateURL()
}

// Remove item handlers
const removeEmployer = (employer: any) => {
  selectedEmployers.value = selectedEmployers.value.filter(emp => emp.id !== employer.id)
  employerFilterTouched.value = true
  updateURL()
}

const removeCountry = (country: any) => {
  selectedCountries.value = selectedCountries.value.filter(c => c.id !== country.id)
  updateURL()
}

const removeLocation = (location: any) => {
  selectedLocations.value = selectedLocations.value.filter(loc => loc.id !== location.id)
  updateURL()
}

// Add infinite scroll handler for employer options
const employerOptionsContainer = ref<HTMLElement | null>(null)
const isLoadingMore = ref(false)

const handleEmployerScroll = async (event: Event) => {
  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container
  
  // Check if we're near the bottom (within 50px) and not already loading
  if (scrollHeight - (scrollTop + clientHeight) < 50 && !isLoadingMore.value && employerStore.nextPage && !employerStore.isLoading) {
    isLoadingMore.value = true
    try {
      await employerStore.loadMoreEmployers()
    } catch (error) {
      console.error('Failed to load more employers:', error)
    } finally {
      isLoadingMore.value = false
    }
  }
}

// Watch for changes in filtered employers to observe the new last item
watch(filteredEmployers, async () => {
  await nextTick()

}, { flush: 'post' })


// Watcher for employer search
let employerSearchTimeout: number | null = null
watch(employerSearch, (newSearch) => {
  if (employerSearchTimeout) {
    clearTimeout(employerSearchTimeout)
  }
  
  employerSearchTimeout = setTimeout(async () => {
    if (newSearch.trim()) {
      await employerStore.searchEmployers(newSearch.trim(), true)
    } else {
      // Reset to initial employers when search is cleared (keep pagination enabled)
      await employerStore.searchEmployers('', true)
    }
  }, 300) as unknown as number
})

// Watchers for filter changes
watch([selectedStatus, selectedDate, selectedExperience], () => {
  if (isInitializingFromURL.value) return
  updateURL()
})

// When employers change due to user interaction, mark as touched and update URL
watch(selectedEmployers, () => {
  if (isInitializingFromURL.value) return
  employerFilterTouched.value = true
  updateURL()
}, { deep: true })

// Other multi-selects update URL immediately
watch([selectedCountries, selectedLocations], () => {
  if (isInitializingFromURL.value) return
  updateURL()
}, { deep: true })

// Clear location selections when countries change
watch(selectedCountries, (newCountries, oldCountries) => {
  if (isInitializingFromURL.value) return
  if (JSON.stringify(newCountries) !== JSON.stringify(oldCountries)) {
    // Filter out locations that don't match the new country selection
    if (selectedLocations.value.length > 0 && newCountries.length > 0) {
      const selectedCountryIds = newCountries.map((country: any) => country.id)
      selectedLocations.value = selectedLocations.value.filter((location: any) => 
        location.country && selectedCountryIds.includes(location.country.id)
      )
    } else if (newCountries.length === 0) {
      // If no countries selected, clear all location selections
      selectedLocations.value = []
    }
  }
}, { deep: true })

// Load initial data
onMounted(async () => {
  await Promise.all([
    jobLocationsStore.fetchJobLocations(),
    // Use paginated search with empty query to enable infinite scrolling
    employerStore.searchEmployers('', true)
  ])
  await initializeFromURL()
})

// Helpers to compare relevant filter query params only
const isScalarEqual = (a: unknown, b: unknown) => {
  const av = a == null ? '' : String(a)
  const bv = b == null ? '' : String(b)
  return av === bv
}

const normalizeArrayField = (v: unknown): string[] => {
  if (v == null) return []
  if (Array.isArray(v)) return v.map(val => String(val))
  return [String(v)]
}

const isArrayFieldEqual = (a: unknown, b: unknown) => {
  const arrA = normalizeArrayField(a).slice().sort()
  const arrB = normalizeArrayField(b).slice().sort()
  if (arrA.length !== arrB.length) return false
  for (let i = 0; i < arrA.length; i++) {
    if (arrA[i] !== arrB[i]) return false
  }
  return true
}

const didRelevantFiltersChange = (newQuery: Record<string, unknown>, oldQuery: Record<string, unknown> | undefined) => {
  if (!oldQuery) return true
  const scalarKeys = ['status', 'date', 'experience']
  for (const key of scalarKeys) {
    if (!isScalarEqual(newQuery[key], oldQuery[key])) return true
  }
  if (!isArrayFieldEqual(newQuery['country_ids'], oldQuery['country_ids'])) return true
  if (!isArrayFieldEqual(newQuery['location_ids'], oldQuery['location_ids'])) return true
  return false
}

// Re-initialize when URL changes externally, but only if relevant filter params changed
watch(() => route.query, async (newQuery, oldQuery) => {
  if (!didRelevantFiltersChange(newQuery as Record<string, unknown>, oldQuery as Record<string, unknown> | undefined)) {
    return
  }
  await initializeFromURL()
}, { deep: true })

</script>